import { Alert, Badge } from '@risk-smart/themed-cloudscape-components';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomisableFieldWrapper from 'src/components/form/form/customisable-form/CustomisableFieldWrapper';
import { CustomisableForm } from 'src/components/form/form/CustomisableForm';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import { ButtonVariant } from 'src/components/form/form/types';
import Loading from 'src/components/loading';
import { useGetActiveAttestationCycle } from 'src/hooks/queries/attestations/useGetActiveAttestationCycle';
import { useGetAttestationConfig } from 'src/hooks/queries/attestations/useGetAttestationConfig';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import {
  useInsertAttestationConfig,
  useInsertAttestationCycle,
} from '@/hooks/mutations/attestation';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import { AttestationFormFields } from '../../forms/AttestationFormFields';
import type { AttestationFormFieldData } from '../../forms/attestationSchema';
import {
  AttestationFormSchema,
  defaultValues,
} from '../../forms/attestationSchema';
import AttestationCycleCardsContainer from './attestation-summary-cards/AttestationSummaryCardsContainer';
import type { ProposedCycle } from './attestation-summary-cards/ProposedAttestationCycleSummary';

type Props = {
  parentDocumentId: string;
};

const Tab: React.FC<Props> = (props: Props) => {
  const {
    hasPermission: canCreateDocument,
    loading: canCreateDocumentLoading,
  } = useHasPermissionQuery('insert:document');

  const useAttestationImprovements = useIsFeatureFlagEnabled(
    'attestation_improvements'
  );

  const { t } = useTranslation(['common']);

  const { data, loading } = useGetAttestationConfig({
    queryArgs: { parentDocumentId: props.parentDocumentId },
  });
  const [values, setValues] = useState<AttestationFormFieldData | undefined>(
    undefined
  );

  const [displayAttestationGroups, setDisplayAttestationGroups] =
    useState<boolean>(false);

  const { data: activeCycle } = useGetActiveAttestationCycle({
    queryArgs: { parentDocumentId: props.parentDocumentId },
  });

  const displayReAttestationRequiredControl = useMemo(() => {
    return (activeCycle?.attestation_cycle?.length ?? 0) > 0;
  }, [activeCycle]);

  const [proposal, setProposal] = useState<ProposedCycle | undefined>(
    undefined
  );

  const hasPublishedDocument = data?.document_file?.some(
    (doc) => doc.PublishedDate
  );

  const { insertAttestationConfig } = useInsertAttestationConfig();
  const { insertAttestationCycle } = useInsertAttestationCycle();

  useEffect(() => {
    const attestation = data?.attestation_config?.[0];

    if (attestation) {
      // COI-670 — hydrate the picker from the stored groups in BOTH branches.
      // The audience is retained but dormant while global attestation is on,
      // so the form must load it either way; otherwise the round trip is
      // lossy and turning the flag back off submits an empty audience.
      const attestationGroups =
        attestation?.groups?.map((group) => ({
          type: 'userGroup' as const,
          value: group.GroupId,
        })) || [];

      if (attestation.RequireGlobalAttestation) {
        setValues({
          requireReattestation: 'true',
          attestationTimeLimit: attestation?.AttestationTimeLimit || null,
          attestationPromptText: attestation?.PromptText || '',
          requireAttestationFromEveryone: 'true',
          attestationGroups,
        });

        return;
      }

      setValues({
        requireReattestation: 'true',
        attestationTimeLimit: attestation?.AttestationTimeLimit || null,
        attestationPromptText: attestation?.PromptText || '',
        requireAttestationFromEveryone: 'false',
        attestationGroups,
      });
    }
  }, [data]);

  const readOnly = !canCreateDocument || canCreateDocumentLoading || loading;

  const onSave = async (formData: AttestationFormFieldData) => {
    if (!hasPublishedDocument) {
      return;
    }

    const requireGlobalAttestation =
      formData.requireAttestationFromEveryone === 'true';

    // COI-670 — send the configured audience whatever the global flag says.
    // Zeroing it here made the flag destructive: `[]` is the wire signal for
    // "clear the audience", so both stacks deleted the rows and toggling the
    // flag back off left the admin rebuilding the set from memory. The
    // `[]`-means-clear contract is unchanged — an explicitly emptied picker
    // still sends `[]`.
    const attestationGroupIds = formData.attestationGroups.map(
      (group) => group.value
    );

    if (useAttestationImprovements) {
      await insertAttestationCycle({
        DocumentId: props.parentDocumentId,
        AllowCarryForward: formData.requireReattestation === 'false',
        AttestationConfig: {
          ParentId: props.parentDocumentId,
          RequireGlobalAttestation: requireGlobalAttestation,
          AttestationTimeLimit: formData.attestationTimeLimit,
          AttestationPromptText: formData.attestationPromptText,
          AttestationGroupIds: attestationGroupIds,
        },
      });
    } else {
      await insertAttestationConfig({
        ParentId: props.parentDocumentId,
        RequireGlobalAttestation: requireGlobalAttestation,
        AttestationTimeLimit: formData.attestationTimeLimit,
        AttestationPromptText: formData.attestationPromptText,
        AttestationGroupIds: attestationGroupIds,
      });
    }
    setValues(formData);
  };

  if (loading) {
    return <Loading />;
  }

  const totalUsers = proposal?.attestationRequiredCount ?? 0;

  return (
    <CustomisableForm
      renderTemplate={(renderProps) => <PageWrapper {...renderProps} />}
      i18n={t('policy')}
      schema={AttestationFormSchema}
      defaultValues={defaultValues}
      formId={'attestation_cycle'}
      parentType={Parent_Type_Enum.AttestationCycle}
      onSave={onSave}
      values={values}
      secondaryActions={undefined}
      header={'Attestations'}
      aside={
        <>
          <AttestationCycleCardsContainer
            parentDocumentId={props.parentDocumentId}
            onProposalChange={setProposal}
            onDisplayAttestationGroupsChange={setDisplayAttestationGroups}
          />
        </>
      }
      submitActions={[
        {
          label: t('distribute'),
          action: onSave,
          variant: ButtonVariant.Primary,
          loading,
          disabled: !hasPublishedDocument,
        },
      ]}
    >
      {!hasPublishedDocument && (
        <Alert
          dismissible={false}
          type={'warning'}
          header={t('attestations.noPublishedDocumentWarningHeading')}
        >
          {t('attestations.noPublishedDocumentWarningMessage')}
        </Alert>
      )}
      <div className={'w-fit'}>
        <Badge color={totalUsers > 0 ? 'blue' : 'grey'}>
          {totalUsers > 0
            ? t('policy.users_affected', { count: totalUsers })
            : t('policy.no_users_affected')}
        </Badge>
      </div>
      <CustomisableFieldWrapper readOnly={readOnly}>
        <AttestationFormFields
          key={'attestationFormFields'}
          totalUsers={proposal?.attestationRequiredCount || 0}
          displayAttestationGroups={displayAttestationGroups}
          displayReAttestationRequiredControl={
            displayReAttestationRequiredControl
          }
        />
      </CustomisableFieldWrapper>
    </CustomisableForm>
  );
};

export default Tab;
